import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Col, Divider, Row, Tooltip, Modal, notification } from "antd";
import {
  PlusOutlined,
  MoreOutlined,
  QuestionCircleFilled,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";

import {
  useGetAdviseeProposals,
  useGetAdviseeProposal,
  postCreateAdviseeOwnedPolicy,
  postCreateAdviseeProposal,
  patchAdviseeProposalSettings,
  deleteAdviseeProposal,
  duplicateAdviseeProposal,
} from "apis/advisee";
import { useFetchConstant } from "apis/constant";

import { buildPNRProposalUri } from "utils/routes";
import {
  constructDisplayPrice,
  constructDisplayNumber,
  getLabelOfConstant,
  convertVariableNameToReadableString,
  guard,
} from "utils/general";

import Title from "components/Title/Title";
import SettingsModal from "./components/SettingsModal/SettingsModal";

import {
  AddProposalButton,
  CardHeaderDescription,
  FieldLabel,
  FieldValue,
  ProposalCard,
  ProposalCardBody,
  SettingContainer,
  ViewMoreBar,
  KeyFieldValue,
  CardTag,
  CardTitle,
} from "./Proposals.styles";
import { ScreenSize } from "utils/constants";
import Locale from "react-intl-universal";

const constructBenefitList = (policy, benefitType) => {
  return [
    {
      label: guard(
        () => benefitType.filter((x) => x.code === "deathBenefit")[0].label,
        "Lifes"
      ),
      value: constructDisplayPrice(policy.deathBenefit, false),
      code: "deathBenefit",
    },
    {
      label: guard(
        () => benefitType.filter((x) => x.code === "tpdBenefit")[0].label,
        "TPD"
      ),
      value: constructDisplayPrice(policy.tpdBenefit, false),
      code: "tpdBenefit",
    },
    {
      label: guard(
        () =>
          benefitType.filter((x) => x.code === "oldAgeDisabilityBenefit")[0]
            .label,
        "Old Age Disability"
      ),
      value: constructDisplayPrice(policy.oldAgeDisabilityBenefit, false),
      code: "oldAgeDisabilityBenefit",
    },
    {
      label: guard(
        () => benefitType.filter((x) => x.code === "ciBenefit")[0].label,
        "Critical Illness"
      ),
      value: constructDisplayPrice(policy.ciBenefit, false),
      code: "ciBenefit",
    },
    {
      label: guard(
        () =>
          benefitType.filter((x) => x.code === "totalEarlyCIBenefit")[0].label,
        "Early Critical Illness"
      ),
      value: constructDisplayPrice(policy.totalEarlyCIBenefit, false),
      code: "totalEarlyCIBenefit",
    },
    {
      label: guard(
        () =>
          benefitType.filter((x) => x.code === "personalAccidentBenefit")[0]
            .label,
        "Personal Accident"
      ),
      value: constructDisplayPrice(policy.personalAccidentBenefit, false),
      code: "personalAccidentBenefit",
    },
    {
      label: guard(
        () => benefitType.filter((x) => x.code === "dailyBenefit")[0].label,
        "Hospitalise Daily Income"
      ),
      value: constructDisplayPrice(policy.dailyBenefit, false),
      code: "dailyBenefit",
    },
    {
      label: Locale.get("proposal.medicalCoverage"),
      value: constructDisplayPrice(policy.medicalCardYearlyLimitAmt, false),
      code: "medicalCardYearlyLimitAmt",
    },
    {
      label: guard(
        () => benefitType.filter((x) => x.code === "ciWaiver")[0].label,
        "CIWP"
      ),
      value: `${policy.ciWaiver ? "Yes" : "No"}`,
      code: "ciWaiver",
    },
    {
      label: guard(
        () => benefitType.filter((x) => x.code === "tpdWaiver")[0].label,
        "TPDWP"
      ),
      value: `${policy.tpdWaiver ? "Yes" : "No"}`,
      code: "tpdWaiver",
    },
    {
      // label: "Medical Card Lifetime limit Amt",
      label: Locale.get("proposal.lifetimeLimit"),
      value: `${
        policy.medicalCardLifetimeLimitAmt === -100 ||
        policy.medicalCardLifetimeLimitAmt === -1
          ? "Unlimited"
          : constructDisplayPrice(policy.medicalCardLifetimeLimitAmt, false)
      }`,
      code: "medicalCardLifetimeLimitAmt",
    },
  ];
};

const Proposal = ({
  data,
  onProposalClick,
  onSettingClick,
  proposalStatusesConst,
  proposalStatuses,
  benefitType,
}) => {
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const benefits = constructBenefitList(data.policyData, benefitType);
  // const addedBenefits = constructBenefitList(data.addedPolicyData);
  const [addedBenefits, setAddedBenefits] = useState(
    constructBenefitList(data.addedPolicyData, benefitType)
  );

  const addedMonthlyPremium = data.addedPolicyData.monthlyPremium || 0;
  const newYearlyPremium = data.policyData.yearlyPremium;
  const deathWaiverPremiumForSelf = data.policyData.deathWaiverPremiumForSelf;
  const coverRatio =
    data.policyData.deathBenefit / (deathWaiverPremiumForSelf * 12);

  const handleOnExpandBarClick = (e) => {
    e.stopPropagation();
    setIsCardExpanded(!isCardExpanded);
  };

  const StatusTag = ({ status }) => {
    let tagColor =
      status === proposalStatusesConst.ARCHIVE.code ? "#4F4F4F" : "#2AC940";

    const statusLabel = getLabelOfConstant(status, proposalStatuses);
    const isStatusActive = status === proposalStatusesConst.ACTIVE.code;

    return isStatusActive ? null : (
      <CardTag color={tagColor}>{statusLabel}</CardTag>
    );
  };

  useEffect(() => {
    renderAdditionalDisplay();
  }, [data]);

  const renderAdditionalDisplay = () => {
    if (!data.display || data.display.length === 0) {
      return;
    }

    let displayFields = data.display;
    let buffer = [...addedBenefits];

    buffer = buffer.filter((value) => {
      if (value.label.substring(0, 6) === "Extra-") {
        return true;
      }
      return displayFields.filter((dis) => dis === value.code).length > 0;
    });

    displayFields = displayFields.filter(
      (dis) => buffer.filter((x) => x.code === dis).length === 0
    );

    for (let i = 0; i < displayFields.length; i++) {
      const display = displayFields[i];

      if (display.substring(0, 6) === "Extra-") {
        const title = display.substring(6, display.length);
        const value = data.addedPolicyData?.extraCoverage?.filter(
          (x) => x.label === title
        );

        if (value && value.length > 0) {
          buffer.push({
            label: `${title}`,
            value: value[0].amount
              ? constructDisplayNumber(value[0].amount)
              : 0,
          });
          continue;
        }
      }

      let findValue = data.addedPolicyData[display];
      if (!findValue) {
        continue;
      }

      const descLabel = getLabelOfConstant(display, benefitType);

      buffer.push({
        label: descLabel
          ? descLabel
          : convertVariableNameToReadableString(display),
        value: constructDisplayNumber(findValue),
      });
    }

    setAddedBenefits(buffer);
  };
  return (
    <ProposalCard
      key={data._id}
      title={
        <CardTitle>
          <StatusTag status={data.status} />
          <Row>
            <FieldLabel span={24}>{data.name}</FieldLabel>
            <Col span={24}>
              <CardHeaderDescription>{data.description}</CardHeaderDescription>
            </Col>
          </Row>
        </CardTitle>
      }
      headStyle={{
        background: "#ffff00",
        minHeight: 60,
        overflow: "visible",
        whiteSpace: "normal",
        textOverflow: "clip",
      }}
      bodyStyle={{ padding: 0, background: data?.theme?.bgColor }}
      extra={
        <SettingContainer onClick={onSettingClick(data._id, data.status)}>
          <MoreOutlined />
        </SettingContainer>
      }
      size="small"
      hoverable
      onClick={onProposalClick(data._id)}
    >
      <ProposalCardBody>
        <div style={{ height: "100%" }} onClick={onProposalClick(data._id)}>
          {!!data.premiumSurrendered && (
            <Row style={{ marginBottom: 8 }}>
              <FieldLabel span={12}>Premium Surrenderred</FieldLabel>
              <FieldValue span={12}>
                <KeyFieldValue backgroundColor="#ffffff">
                  {constructDisplayPrice(data.premiumSurrendered, false)}
                </KeyFieldValue>
              </FieldValue>
            </Row>
          )}
          {addedBenefits.map((benefit) => (
            <Row key={benefit.label}>
              <FieldLabel span={12}>{benefit.label}</FieldLabel>
              <FieldValue span={12}>{benefit.value}</FieldValue>
            </Row>
          ))}

          <Row style={{ margin: "8px 0px" }}>
            <FieldLabel span={12}>{Locale.get("proposal.premium")}</FieldLabel>
            <FieldValue span={12}>
              <KeyFieldValue backgroundColor="#d7a301">
                {constructDisplayPrice(addedMonthlyPremium * 12, false)}
                <Tooltip
                  placement="topLeft"
                  title={`${constructDisplayPrice(
                    addedMonthlyPremium,
                    false
                  )} ${Locale.get("proposal.perMonth")}`}
                >
                  <QuestionCircleFilled style={{ marginLeft: 4 }} />
                </Tooltip>
              </KeyFieldValue>
            </FieldValue>
          </Row>
        </div>
        {isCardExpanded && (
          <>
            <Divider style={{ margin: "16px 0px" }} />
            <div
              style={{
                fontWeight: "bold",
                textDecoration: "underline",
                marginBottom: 4,
              }}
            >
              {Locale.get("proposal.newCover")}
            </div>

            {benefits.map((benefit) => (
              <Row key={benefit.label}>
                <FieldLabel span={12}>{benefit.label}</FieldLabel>
                <FieldValue span={12}>{benefit.value}</FieldValue>
              </Row>
            ))}
            <Row style={{ margin: "8px 0px" }}>
              <FieldLabel span={12}>
                {Locale.get("proposal.premium")}
              </FieldLabel>
              <FieldValue span={12}>
                <KeyFieldValue backgroundColor="#ffff00">
                  {constructDisplayPrice(newYearlyPremium, false)}
                  <Tooltip
                    placement="topLeft"
                    title={`${constructDisplayPrice(
                      newYearlyPremium / 12,
                      false
                    )} ${Locale.get("proposal.perMonth")}`}
                  >
                    <QuestionCircleFilled style={{ marginLeft: 4 }} />
                  </Tooltip>
                </KeyFieldValue>
              </FieldValue>
            </Row>
            <Row style={{ marginBottom: 8 }}>
              <FieldLabel span={12}>
                {Locale.get("proposal.coverRatio")}
              </FieldLabel>
              <FieldValue span={12}>
                <FieldLabel>{constructDisplayNumber(coverRatio, 2)}</FieldLabel>
              </FieldValue>
            </Row>

            {data.remarks && (
              <>
                <b>{Locale.get("proposal.remarks")} : </b>
                {data.remarks}
              </>
            )}
          </>
        )}
      </ProposalCardBody>
      <ViewMoreBar onClick={handleOnExpandBarClick}>
        {isCardExpanded ? (
          <>
            {Locale.get("proposal.lessDetail")}
            <UpOutlined style={{ marginLeft: 8, fontSize: 8 }} />
          </>
        ) : (
          <>
            {Locale.get("proposal.moreDetail")}
            <DownOutlined style={{ marginLeft: 8, fontSize: 8 }} />
          </>
        )}
      </ViewMoreBar>
    </ProposalCard>
  );
};

const Proposals = ({
  insurancesInfo = {},
  currentReporting,
  isSettingsModalVisible,
  setIsSettingsModalVisible,
  adviseeDetails,
}) => {
  // console.log('insurancesInfo', insurancesInfo);
  console.log("currentReporting", currentReporting);

  const { id: adviseeId } = useParams();
  const history = useHistory();

  const [selectedSettings, setSelectedSettings] = useState();
  const [maxPosition, setMaxPosition] = useState(0);

  const [assignProposalId, setAssignProposalId] = useState();

  const {
    isLoading: isProposalsLoading,
    data: proposals,
  } = useGetAdviseeProposals(adviseeId);

  const {
    isLoading: isProposalDetailsLoading,
    data: proposalDetails,
    refetch: refetchProposalDetails,
  } = useGetAdviseeProposal(adviseeId, assignProposalId);

  console.log("proposals", proposals);
  console.log("proposalDetails", proposalDetails);

  // const [proposals, setProposals] = useState({
  //   active: [],
  //   archive: [],
  //   deal: [],
  // });

  // useEffect(() => {
  //   var FilterProposal = { active: [], archive: [], deal: [] };

  //   if (dataProposals) {
  //     Object.keys(dataProposals).forEach((key) => {
  //       dataProposals[key].forEach((val) => {
  //         FilterProposal[key].push({
  //           ...val,
  //           theme: val.theme ? val.theme : { bgColor: "#E93324" },
  //         });
  //       });
  //     });

  //     console.log("FilterProposal", FilterProposal);

  //     setProposals(FilterProposal);
  //   }
  // }, [dataProposals]);

  const {
    isLoading: isBenefitTypeloading,
    selection: benefitType,
  } = useFetchConstant("benefitTypes");
  const {
    isLoading: isProposalStatusesLoading,
    selection: proposalStatuses,
    data: proposalStatusesConst,
  } = useFetchConstant("proposalStatuses");
  const {
    isLoading: isProposalCardThemesLoading,
    selection: proposalCardThemes,
  } = useFetchConstant("proposalCardThemes", "colorCode");

  const isNewProposalSettings = useMemo(
    () => !selectedSettings || Object.keys(selectedSettings).length === 0,
    [selectedSettings]
  );
  const isLoading =
    isProposalsLoading ||
    isProposalStatusesLoading ||
    isProposalCardThemesLoading;

  const handleOnAddProposalButtonClick = () => {
    setIsSettingsModalVisible(true);
    setMaxPosition(proposals[proposalStatusesConst.ACTIVE.code].length);
  };

  const handleOnProposalCardClick = (proposalId) => () =>
    history.push(buildPNRProposalUri(adviseeId, proposalId));

  const handleOnSettingClick = (proposalId, status) => (e) => {
    e.stopPropagation();

    setAssignProposalId(proposalId);

    setTimeout(() => {
      refetchProposalDetails();
    }, 100);

    const selectedProposal = proposals[status].find(
      (proposal) => proposal._id === proposalId
    );

    if (selectedProposal) {
      const proposalSettings = {
        _id: selectedProposal._id,
        name: selectedProposal.name,
        display: selectedProposal.display,
        description: selectedProposal.description,
        theme: selectedProposal.theme,
        status: selectedProposal.status,
        remarks: selectedProposal.remarks,
        position: proposals[status].findIndex(
          (proposal) => proposal._id === proposalId
        ),
        addedPolicyData: selectedProposal.addedPolicyData,
      };

      setSelectedSettings(proposalSettings);
      setMaxPosition(proposals[status].length);
      setIsSettingsModalVisible(true);
    }
  };

  const handleOnSettingsModalClose = () => {
    setIsSettingsModalVisible(false);
    setSelectedSettings();
    setMaxPosition(0);
  };

  const handleOnSettingsModalSave = (proposalPayload, joinDatePayload) => {
    console.log("proposalPayload", proposalPayload);
    console.log("joinDatePayload", joinDatePayload);

    if (isNewProposalSettings) {
      postCreateAdviseeProposal(adviseeId, proposalPayload).then(
        (newProposal) => {
          history.push(buildPNRProposalUri(adviseeId, newProposal._id));
        }
      );
    } else {
      patchAdviseeProposalSettings(adviseeId, selectedSettings._id, {
        proposalPayload,
        joinDatePayload,
      });

      if (
        proposalPayload.status === "deal" &&
        proposalDetails.addedProposedPolicies &&
        proposalDetails.addedProposedPolicies.length > 0
      ) {
        Modal.confirm({
          title:
            "Congratulations! Automatically add proposed policy to New Policies?",
          onOk: () => {
            notification.warning({
              message: "Adding Proposed New Policies",
              description:
                "Please do not close this tab until the process is completed.",
              duration: 0,
            });

            if (
              proposalDetails.addedProposedPolicies &&
              proposalDetails.addedProposedPolicies.length > 0
            ) {
              proposalDetails.addedProposedPolicies.forEach(
                async (policy, index, array) => {
                  console.log("policyIndex " + index, policy, index);

                  setTimeout(async () => {
                    const res = await postCreateAdviseeOwnedPolicy(
                      adviseeId,
                      policy
                    );

                    console.log(473, res);

                    if (res) {
                      if (index === array.length - 1) {
                        notification.destroy();

                        Modal.success({
                          title: "Policy has been added!",
                        });
                      }
                    }
                  }, 3000 * index + 1);
                }
              );
            }
          },
        });
      }
    }
    handleOnSettingsModalClose();
  };

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const handleOnSettingsModalDuplicate = (proposalId, payload) => {
    duplicateAdviseeProposal(adviseeId, proposalId, payload).then(() => {
      handleOnSettingsModalClose();
    });
  };

  const handleOnDelete = (proposalId) => {
    deleteAdviseeProposal(adviseeId, proposalId).then(() => {
      handleOnSettingsModalClose();
    });
  };

  const ExistingCover = (
    <div
      style={{
        border: "2px solid rgba(0,0,0,0.65)",
      }}
    >
      <Row
        justify="center"
        style={{
          fontSize: 22,
          fontWeight: "bold",
          color: "black",
          padding: 12,
          borderBottom: "2px solid rgba(0,0,0,0.65)",
        }}
      >
        {Locale.get("proposal.existingCover")}
      </Row>

      <div
        style={{
          padding: 12,
          fontWeight: 500,
          color: "black",
          borderBottom: "2px solid rgba(0,0,0,0.65)",
        }}
      >
        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>{Locale.get("proposal.death")}</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.deathBenefit ? insurancesInfo.deathBenefit : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>{Locale.get("proposal.tpd")}</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.tpdBenefit ? insurancesInfo.tpdBenefit : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>{Locale.get("proposal.ci")}</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.ciBenefit ? insurancesInfo.ciBenefit : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>{Locale.get("proposal.eci")}</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.totalEarlyCIBenefit
                ? insurancesInfo.totalEarlyCIBenefit
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>{Locale.get("proposal.personalAccident")}</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.personalAccidentBenefit
                ? insurancesInfo.personalAccidentBenefit
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>{Locale.get("proposal.medicalCoverage")}</Col>

          {/* <Col>0</Col> */}
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col /* offset={6} */>{Locale.get("proposal.roomAndBoard")}</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.medicalCardRoomAndBoardAmt
                ? insurancesInfo.medicalCardRoomAndBoardAmt
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col /* offset={6} */>{Locale.get("proposal.annualLimit")}</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.medicalCardYearlyLimitAmt
                ? insurancesInfo.medicalCardYearlyLimitAmt
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col /* offset={6} */>{Locale.get("proposal.lifetimeLimit")}</Col>

          <Col>
            {insurancesInfo.medicalCardLifetimeLimitAmt === -100
              ? "Unlimited"
              : constructDisplayPrice(
                  insurancesInfo.medicalCardLifetimeLimitAmt
                    ? insurancesInfo.medicalCardLifetimeLimitAmt
                    : 0,
                  false
                )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>{Locale.get("proposal.premium")}</Col>

          <Col>
            {constructDisplayPrice(
              insurancesInfo.yearlyPremium ? insurancesInfo.yearlyPremium : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between">
          <Col>{Locale.get("proposal.coverRatio")}</Col>

          <Col>
            {constructDisplayPrice(
              currentReporting?.riskRatioData?.naturalDeath?.coverRatio?.value
                ? currentReporting.riskRatioData.naturalDeath.coverRatio.value
                : 0,
              false
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          padding: 12,
          fontWeight: 500,
          color: "black",
        }}
      >
        <Row
          justify="center"
          style={{ fontWeight: "bold", fontsize: 16, marginBottom: 12 }}
        >
          {Locale.get("proposal.grossInsuranceNeed")}
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>
            {Locale.get("proposal.deathFor")}{" "}
            {currentReporting?.grossInsNeeds?.deathCover?.yearsOfCoverage}{" "}
            {Locale.get("proposal.years")}
          </Col>

          <Col>
            {constructDisplayPrice(
              currentReporting?.grossInsNeeds?.deathCover?.lifeInsNeeds
                ? currentReporting.grossInsNeeds.deathCover.lifeInsNeeds
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>
            {Locale.get("proposal.tpdFor")}{" "}
            {currentReporting?.grossInsNeeds?.tpd?.yearsOfCoverage}{" "}
            {Locale.get("proposal.years")}
          </Col>

          <Col>
            {constructDisplayPrice(
              currentReporting?.grossInsNeeds?.tpd?.lifeInsNeeds
                ? currentReporting.grossInsNeeds.tpd.lifeInsNeeds
                : 0,
              false
            )}
          </Col>
        </Row>

        <Row justify="space-between">
          <Col>
            {Locale.get("proposal.ciFor")}{" "}
            {currentReporting?.grossInsNeeds?.ci?.yearsOfCoverage}{" "}
            {Locale.get("proposal.years")}
          </Col>

          <Col>
            {constructDisplayPrice(
              currentReporting?.grossInsNeeds?.ci?.lifeInsNeeds
                ? currentReporting.grossInsNeeds.ci.lifeInsNeeds
                : 0,
              false
            )}
          </Col>
        </Row>
      </div>
    </div>
  );

  return (
    <div
      style={{
        width: window.innerWidth < ScreenSize.xl && 1489,
      }}
    >
      {!isLoading && Object.keys(proposals).length ? (
        <>
          <div>
            <Title marginBottom="16px">
              {Locale.get("proposal.solutionReprogrammingProposal")}
            </Title>
            <Row gutter={[24, 24]}>
              <Col xl={6} md={6} xs={6}>
                {ExistingCover}
              </Col>

              <Col xl={18} md={18} xs={18}>
                <Row gutter={[24, 24]}>
                  {proposals[proposalStatusesConst.ACTIVE.code].map(
                    (proposal, index) => (
                      <Col
                        xl={8}
                        md={8}
                        xs={8}
                        key={proposal._id}
                        order={index}
                      >
                        <Proposal
                          data={proposal}
                          onProposalClick={handleOnProposalCardClick}
                          onSettingClick={handleOnSettingClick}
                          proposalStatusesConst={proposalStatusesConst}
                          proposalStatuses={proposalStatuses}
                          benefitType={benefitType}
                        />
                      </Col>
                    )
                  )}

                  <Col
                    xl={8}
                    md={8}
                    xs={8}
                    order={proposals[proposalStatusesConst.ACTIVE.code].length}
                  >
                    <AddProposalButton
                      onClick={handleOnAddProposalButtonClick}
                      hoverable
                    >
                      <PlusOutlined /> {Locale.get("proposal.addProposal")}
                    </AddProposalButton>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>

          {proposals[proposalStatusesConst.DEAL.code].length ? (
            <div>
              <Title marginBottom="16px">
                {Locale.get("proposal.dealProposals")}
              </Title>
              <Row gutter={[24, 24]}>
                {proposals[proposalStatusesConst.DEAL.code].map(
                  (proposal, index) => (
                    <Col xl={6} md={6} xs={6} key={proposal._id} order={index}>
                      <Proposal
                        data={proposal}
                        onProposalClick={handleOnProposalCardClick}
                        onSettingClick={handleOnSettingClick}
                        proposalStatusesConst={proposalStatusesConst}
                        proposalStatuses={proposalStatuses}
                        benefitType={benefitType}
                      />
                    </Col>
                  )
                )}
              </Row>
            </div>
          ) : null}
          {proposals[proposalStatusesConst.ARCHIVE.code].length ? (
            <div>
              <Title marginBottom="16px">
                {Locale.get("proposal.archievedProposals")}
              </Title>
              <Row gutter={[24, 24]}>
                {proposals[proposalStatusesConst.ARCHIVE.code].map(
                  (proposal, index) => (
                    <Col xl={6} md={6} xs={6} key={proposal._id} order={index}>
                      <Proposal
                        data={proposal}
                        onProposalClick={handleOnProposalCardClick}
                        onSettingClick={handleOnSettingClick}
                        proposalStatusesConst={proposalStatusesConst}
                        proposalStatuses={proposalStatuses}
                        benefitType={benefitType}
                      />
                    </Col>
                  )
                )}
              </Row>
            </div>
          ) : null}

          <SettingsModal
            values={selectedSettings}
            adviseeDetails={adviseeDetails}
            visible={isSettingsModalVisible}
            onClose={handleOnSettingsModalClose}
            onSave={handleOnSettingsModalSave}
            onDelete={handleOnDelete}
            onDuplicate={handleOnSettingsModalDuplicate}
            isNew={isNewProposalSettings}
            maxPosition={maxPosition}
            proposalStatuses={proposalStatuses}
            proposalStatusesConst={proposalStatusesConst}
            proposalCardThemes={proposalCardThemes}
          />
        </>
      ) : (
        <div>
          <Title marginBottom="16px">
            {Locale.get("proposal.solutionReprogrammingProposal")}
          </Title>
          <Col xl={6} md={6} xs={6}>
            <AddProposalButton
              onClick={handleOnAddProposalButtonClick}
              hoverable
            >
              <PlusOutlined /> {Locale.get("proposal.addProposal")}
            </AddProposalButton>
          </Col>
        </div>
      )}
    </div>
  );
};

export default Proposals;
